/**
 * AudioChunkRecorder — MediaRecorder-based chunked audio capture for pre-recorded mode.
 *
 * Records audio in chunks using MediaRecorder with webm/opus encoding.
 * Supports variable chunk durations via chunkDurationRamp (60s → 90s → 2min → 3min).
 * Each completed chunk fires an onChunkReady callback for IndexedDB backup and
 * rolling upload/transcription. On stop, returns all chunks as an ordered array.
 *
 * See: docs/features/document-system/audio-quality-gates.md (Phase 1)
 * See: docs/features/integrations/deepgram-prerecorded.md
 */

import { chunkDurationRamp, preRecordedChunkDurationMs, preRecordedOverlapMs, preRecordedRollingTranscription } from "../globals";

// A valid MediaRecorder WebM chunk contains more than the container headers alone.
// The production failure logged as 0.0KB, so this also catches tiny malformed blobs
// that round down to zero in the recorder log.
export const MIN_AUDIO_CHUNK_BYTES = 64;

export class AudioChunkRecorder {
  /**
   * @param {MediaStream} stream - Mic stream from getUserMedia
   * @param {object} options
   * @param {function} options.onChunkReady - Called with { blob, index, timestamp, durationMs } for each completed chunk
   * @param {function} options.onError - Called with Error on failure
   * @param {function} options.onChunkSkipped - Called when a rotation contains no usable audio
   */
  constructor(stream, { onChunkReady, onError, onChunkSkipped } = {}) {
    this.stream = stream;
    this.onChunkReady = onChunkReady || (() => {});
    this.onError = onError || console.error;
    this.onChunkSkipped = onChunkSkipped || (() => {});
    this.chunks = [];
    this.currentParts = [];
    this.chunkIndex = 0;
    this.recorder = null;
    this.chunkTimer = null;
    this.isRecording = false;
    this.isStopping = false;
    this.isPaused = false;
    this._chunkTimerRemaining = null;
    this._chunkRotationTarget = null;
    this._chunkActiveStartedAt = null;
    this._chunkDurationMs = 0;

    // Determine best supported mime type
    this.mimeType = 'audio/webm;codecs=opus';
    if (typeof MediaRecorder !== 'undefined' && !MediaRecorder.isTypeSupported(this.mimeType)) {
      this.mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported(this.mimeType)) {
        this.mimeType = ''; // Let browser pick default
      }
    }
  }

  /**
   * Start recording. Begins first chunk immediately.
   */
  start() {
    if (this.isRecording) return;
    this.isRecording = true;
    this.isStopping = false;
    this.chunks = [];
    this.chunkIndex = 0;
    console.log(`[AudioChunkRecorder] Starting recording, mimeType: ${this.mimeType}`);
    this._startNewChunk();
  }

  /**
   * Stop recording and return all captured chunks.
   * @returns {Promise<Array<{ blob: Blob, index: number, timestamp: number, durationMs: number }>>}
   */
  stop() {
    return new Promise((resolve) => {
      if (!this.isRecording) {
        resolve(this.chunks);
        return;
      }

      this.isStopping = true;
      this.isRecording = false;
      this._captureChunkDuration();

      // Clear the chunk rotation timer
      if (this.chunkTimer) {
        clearTimeout(this.chunkTimer);
        this.chunkTimer = null;
      }

      if (this.recorder && this.recorder.state !== 'inactive') {
        // When the recorder stops, onstop will finalize the last chunk
        this.recorder.onstop = () => {
          this._finalizeCurrentChunk();
          console.log(`[AudioChunkRecorder] Stopped. Total chunks: ${this.chunks.length}`);
          resolve(this.chunks);
        };
        this.recorder.stop();
      } else {
        // Recorder already inactive, finalize if there are pending parts
        if (this.currentParts.length > 0) {
          this._finalizeCurrentChunk();
        }
        resolve(this.chunks);
      }
    });
  }

  /**
   * Cancel recording and discard all chunks.
   */
  cancel() {
    this.isRecording = false;
    this.isStopping = false;
    this.isPaused = false;

    if (this.chunkTimer) {
      clearTimeout(this.chunkTimer);
      this.chunkTimer = null;
    }

    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.onstop = null;
      this.recorder.ondataavailable = null;
      this.recorder.stop();
    }

    this.chunks = [];
    this.currentParts = [];
    this.recorder = null;
    console.log('[AudioChunkRecorder] Recording cancelled, all chunks discarded');
  }

  /**
   * Force rotate to a new chunk (e.g., after mic swap).
   * Clears the scheduled rotation timer and immediately rotates.
   */
  rotateChunk() {
    if (this.chunkTimer) {
      clearTimeout(this.chunkTimer);
      this.chunkTimer = null;
    }
    this._rotateChunk();
  }

  /**
   * Pause recording — stops capturing audio, freezes chunk rotation timer.
   * MediaRecorder stays in "paused" state; no audio data is collected.
   */
  pause() {
    if (!this.isRecording || this.isPaused) return;
    this._captureChunkDuration();
    this.isPaused = true;

    // Pause the chunk rotation timer
    if (this.chunkTimer) {
      this._chunkTimerRemaining = (this._chunkRotationTarget || Date.now()) - Date.now();
      clearTimeout(this.chunkTimer);
      this.chunkTimer = null;
    }

    // Pause the MediaRecorder (native API — stops firing ondataavailable)
    if (this.recorder && this.recorder.state === 'recording') {
      this.recorder.pause();
    }

    console.log('[AudioChunkRecorder] Paused');
  }

  /**
   * Resume recording after a pause.
   */
  resume() {
    if (!this.isRecording || !this.isPaused) return;
    this.isPaused = false;

    // Resume the MediaRecorder
    if (this.recorder && this.recorder.state === 'paused') {
      this.recorder.resume();
      this._chunkActiveStartedAt = Date.now();
    }

    // Restart chunk rotation timer with remaining time
    const remaining = Math.max(0, this._chunkTimerRemaining ?? (this._getChunkDuration(this.chunkIndex) + preRecordedOverlapMs));
    this._chunkRotationTarget = Date.now() + remaining;
    this.chunkTimer = setTimeout(() => {
      this._rotateChunk();
    }, remaining);
    this._chunkTimerRemaining = null;

    console.log(`[AudioChunkRecorder] Resumed, next rotation in ${(remaining / 1000).toFixed(1)}s`);
  }

  /**
   * Get the chunk duration for a given chunk index.
   * When rolling transcription is enabled, uses the ramp schedule (60s → 90s → 2min → 3min).
   * When disabled (kill switch), falls back to the fixed 5-minute duration.
   * @param {number} index - Chunk index (0-based)
   * @returns {number} Duration in milliseconds
   * @private
   */
  _getChunkDuration(index) {
    if (!preRecordedRollingTranscription) {
      return preRecordedChunkDurationMs;
    }
    return chunkDurationRamp[Math.min(index, chunkDurationRamp.length - 1)];
  }

  /** @private */
  _startNewChunk() {
    if (!this.isRecording || this.isStopping) return;

    this.currentParts = [];

    try {
      const options = this.mimeType ? { mimeType: this.mimeType } : {};
      this.recorder = new MediaRecorder(this.stream, options);
    } catch (err) {
      this.onError(err);
      return;
    }

    this.recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this.currentParts.push(event.data);
      }
    };

    this.recorder.onerror = (event) => {
      console.error('[AudioChunkRecorder] MediaRecorder error:', event.error);
      this.onError(event.error);
    };

    // Use timeslice of 1000ms for responsive ondataavailable
    this.recorder.start(1000);
    this._chunkActiveStartedAt = Date.now();
    this._chunkDurationMs = 0;

    // Schedule chunk rotation: variable chunkDuration + overlap
    const chunkDuration = this._getChunkDuration(this.chunkIndex);
    const rotationMs = chunkDuration + preRecordedOverlapMs;
    this._chunkRotationTarget = Date.now() + rotationMs;
    this.chunkTimer = setTimeout(() => {
      this._rotateChunk();
    }, rotationMs);

    console.log(`[AudioChunkRecorder] Chunk ${this.chunkIndex} started (${chunkDuration / 1000}s + ${preRecordedOverlapMs / 1000}s overlap), rotation in ${rotationMs / 1000}s`);
  }

  /** @private */
  _rotateChunk() {
    if (!this.isRecording || this.isStopping) return;

    // Stop current recorder — onstop will finalize and start next
    if (this.recorder && this.recorder.state !== 'inactive') {
      this._captureChunkDuration();
      this.recorder.onstop = () => {
        this._finalizeCurrentChunk();
        this._startNewChunk();
      };
      this.recorder.stop();
    }
  }

  /** @private */
  _captureChunkDuration() {
    if (this._chunkActiveStartedAt !== null) {
      this._chunkDurationMs += Math.max(0, Date.now() - this._chunkActiveStartedAt);
      this._chunkActiveStartedAt = null;
    }
  }

  /** @private */
  _finalizeCurrentChunk() {
    if (this.currentParts.length === 0) {
      this._skipCurrentChunk(0);
      return false;
    }

    const blob = new Blob(this.currentParts, {
      type: this.mimeType || 'audio/webm',
    });

    if (blob.size < MIN_AUDIO_CHUNK_BYTES) {
      this._skipCurrentChunk(blob.size);
      return false;
    }

    const chunkData = {
      blob,
      index: this.chunkIndex,
      timestamp: Date.now(),
      durationMs: this._chunkDurationMs,
    };

    this.chunks.push(chunkData);
    console.log(`[AudioChunkRecorder] Chunk ${this.chunkIndex} finalized, size: ${blob.size} bytes (${(blob.size / 1024).toFixed(1)}KB)`);

    // Fire callback for IndexedDB backup
    this.onChunkReady(chunkData);

    this.chunkIndex++;
    this.currentParts = [];
    return true;
  }

  /** @private */
  _skipCurrentChunk(size) {
    const skippedChunk = {
      index: this.chunkIndex,
      size,
      timestamp: Date.now(),
    };
    console.warn(`[AudioChunkRecorder] Skipping unusable chunk ${this.chunkIndex}, size: ${size} bytes`);
    this.currentParts = [];
    this.onChunkSkipped(skippedChunk);
  }
}
